import { type ElementType, useEffect, useMemo, useRef } from "react";

import { type Action, useRegisterActions } from "kbar";
import { isEqual } from "lodash";

import { useSetCommandBarPageContext } from "src/components/commandbar/context";
import { useAppLevelNavigate } from "src/router";
import type {
  CommandBarPageContext,
  CommandBarPageObject,
} from "src/components/commandbar/types";

export type CommandBarContextAction = {
  id: string;
  name: string;
  icon: ElementType;
  // One lowercase key. It also works on the page while focus is outside text fields.
  shortcut?: string;
} & (
  | {
      // A workspace path, which the palette opens as its own navigation, so open settings close first.
      href: string;
      perform?: never;
    }
  | { perform: () => void; href?: never }
);

type Args = {
  object: CommandBarPageObject;
  actions: CommandBarContextAction[];
};

// kbar skips text fields only; these widgets take single keys too, for typeahead or selection.
const KEY_TAKING_SELECTOR =
  'select, [role="combobox"], [role="listbox"], [role="menu"], [role="option"], [role="slider"], [role="spinbutton"]';

// Single-key shortcuts must not act on the page underneath an open modal, or steal a key a focused widget takes.
const pageTakesKeys = () =>
  !document.activeElement?.closest(KEY_TAKING_SELECTOR) &&
  !document.querySelector('[aria-modal="true"]');

export const useDeepStable = <T>(value: T): T => {
  const ref = useRef(value);
  if (!isEqual(ref.current, value)) {
    ref.current = value;
  }
  return ref.current;
};

// The detail page tab a path like .../syncs/<id>/<tab> shows. A deeper path, such as one run under Runs, shows no tab's own page.
export const getDetailTab = (
  pathname: string,
  objectId: string,
): string | undefined => {
  const segments = pathname.split("/").filter(Boolean);
  // The last match: a numeric workspace slug can equal the id, and it comes first.
  const idIndex = segments.lastIndexOf(objectId);
  return idIndex === -1 || segments.length > idIndex + 2
    ? undefined
    : segments[idIndex + 1];
};

// Makes `object` the palette's context chip and lists `actions` first when nothing is typed.
export const useCommandBarContext = ({ object, actions }: Args) => {
  const setPageContext = useSetCommandBarPageContext();

  const navigate = useAppLevelNavigate();
  const performers = useRef(new Map<string, () => void>());
  performers.current = new Map(
    actions.map(({ id, href, perform }) => [
      id,
      href === undefined ? perform : () => navigate(href),
    ]),
  );

  // Callers pass fresh values every render; register again only when what they describe changes.
  const stableObject = useDeepStable(object);
  const stableActions = useDeepStable(
    actions.map(({ id, name, icon, shortcut }) => ({
      id,
      name,
      icon,
      shortcut,
    })),
  );

  const kbarActions = useMemo<Action[]>(
    () =>
      stableActions.map(({ id, name, shortcut }) => ({
        id,
        name,
        shortcut: shortcut ? [shortcut] : undefined,
        perform: () => performers.current.get(id)?.(),
      })),
    [stableActions],
  );
  useRegisterActions(kbarActions, [kbarActions]);

  // kbar prevents a shortcut key's default before it performs, so stop a key the page does not take on document, before kbar's window listener.
  useEffect(() => {
    const keys = new Set(
      stableActions.flatMap(({ shortcut }) => shortcut ?? []),
    );
    if (!keys.size) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      const hasModifier = event.metaKey || event.ctrlKey || event.altKey;
      if (
        !hasModifier &&
        keys.has(event.key.toLowerCase()) &&
        !pageTakesKeys()
      ) {
        event.stopPropagation();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [stableActions]);

  useEffect(() => {
    const context: CommandBarPageContext = {
      object: stableObject,
      actions: stableActions,
    };
    setPageContext(context);
    return () =>
      setPageContext((current) => (current === context ? null : current));
  }, [stableObject, stableActions, setPageContext]);
};
